import {
  Box,
  Container,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  Avatar,
  Paper, 
  IconButton,
  Menu,
  MenuItem,
  Skeleton
} from "@mui/material";
import {
  LocalShipping,
  Schedule,
  Visibility,
  AssignmentTurnedIn,
  Inventory2,
  MoveToInbox,
  TaskAlt
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useState, useEffect, useCallback } from "react";
// import ReviewModal from "../../Components/ReviewModal/ReviewModal";
import { postData } from "../../Webservice/Webservice";
import { Alert } from "@mui/material";
import MoreVertIcon from "@mui/icons-material/MoreVert";

function MyOrders() {
  const navigate = useNavigate();
  // const [reviewModal, setReviewModal] = useState({
  //   open: false,
  //   orderId: null,
  // });
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterMenuAnchor, setFilterMenuAnchor] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");

  const fetchOrders = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const tokenValue = sessionStorage.getItem("token");
      const token = tokenValue ? `Bearer ${tokenValue}` : "";
      
      if (!token) {
        setError("Please login to view your orders");
        setLoading(false);
        return;
      }

      const response = await postData(
        "order/order-list",
        { "appStatus": selectedStatus ? [selectedStatus] : [""] },
        token,
        false 
      );

      if (response && response.orders) {
        // Debug: Log first order to check structure
        if (response.orders.length > 0) {
          console.log("Sample order from API:", response.orders[0]);
        }
        
        // Transform API response to match component structure
        const transformedOrders = response.orders.map((order) => {
          // Calculate total from items
          const total = order.items.reduce((sum, item) => sum + (item.total || 0), 0);
          
          // Transform items
          const transformedItems = order.items.map((item) => ({
            name: item.product_id?.title || "Product",
            image: item.product_id?.product_images?.[0] || "",
            price: item.price || 0,
            quantity: item.quantity || 1,
          }));

          // Format date
          const orderDate = new Date(order.createdAt);
          const formattedDate = orderDate.toLocaleDateString("en-GB", {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
          });

          // Ensure _id exists
          if (!order._id) {
            console.warn("Order _id is missing:", order);
          }

          return {
            id: `#${order.orderNumber}`,
            _id: order._id || order.id, // Store the MongoDB _id for API calls, fallback to id
            date: formattedDate,
            status: order.appStatus || "confirmed",
            items: transformedItems,
            total: total.toFixed(2),
            tracking: order.orderNumber || order._id,
          };
        });

        setOrders(transformedOrders);
      } else {
        setOrders([]);
      }
    } catch (err) {
      console.error("Error fetching orders:", err);
      setError(err.message || "Failed to fetch orders");
      setOrders([]);
    } finally {
      setLoading(false);
    }
  }, [selectedStatus]);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders, selectedStatus]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "confirmed":
        return <AssignmentTurnedIn sx={{ color: "white" }} />; // Blue - confirmed
      
    case "packed":
      return <Inventory2 sx={{ color: "white" }} />; // Purple - packed

    case "shipped":
      return <LocalShipping sx={{ color: "white" }} />; // Teal - shipped

    case "out-for-delivery":
      return <MoveToInbox sx={{ color: "white" }} />; // Yellow - out for delivery

    case "delivered":
      return <TaskAlt sx={{ color: "white" }} />; // Green - delivered

    default:
      return <Schedule sx={{ color: "white" }} />; // Default
  }
};

  const handleFilterMenuOpen = (event) => {
    setFilterMenuAnchor(event.currentTarget);
  };

  const handleFilterMenuClose = () => {
    setFilterMenuAnchor(null);
  };

  const handleStatusSelect = (status) => {
    setSelectedStatus(status);
    handleFilterMenuClose();
  };

  // const handleOpenReview = (orderId) => {
  //   setReviewModal({
  //     open: true,
  //     orderId,
  //   });
  // };

  // const handleCloseReview = () => {
  //   setReviewModal({
  //     open: false,
  //     orderId: null,
  //   });
  // };

  // const handleSubmitReview = (reviewData) => {
  //   console.log("Review submitted:", {
  //     orderId: reviewModal.orderId,
  //     ...reviewData,
  //   });
  // };

  // Skeleton component for order items
  const OrderSkeleton = () => (
    <Paper
      elevation={1}
      sx={{
        bgcolor: "white",
        overflow: "hidden",
        boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
        borderRadius: "20px"
      }}
    >
      {/* Order Header Skeleton */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Grid container alignItems="center" sx={{ justifyContent: "space-between" }} spacing={2}>
          <Grid item xs={12} sm={6}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <Skeleton variant="circular" width={42} height={42} />
              <Box sx={{ flexGrow: 1 }}>
                <Skeleton variant="text" width="60%" height={24} />
                <Skeleton variant="text" width="80%" height={16} sx={{ mt: 0.5 }} />
              </Box>
            </Box>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Box
              sx={{
                display: "flex",
                justifyContent: { xs: "flex-start", sm: "flex-end" },
                alignItems: "center",
                gap: 1.5,
                flexWrap: "wrap",
              }}
            >
              <Skeleton variant="rectangular" width={100} height={24} sx={{ borderRadius: "20px" }} />
              <Skeleton variant="text" width={60} height={20} />
            </Box>
          </Grid>
        </Grid>
      </Box>
      <Divider />
      {/* Order Items Skeleton */}
      <Box sx={{ p: { xs: 2, sm: 3 } }}>
        <Skeleton variant="text" width="30%" height={20} sx={{ mb: 1.5 }} />
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
          {/* First item skeleton */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              borderRadius: "10px",
              border: "1px solid #f0f0f0",
              flexDirection: { xs: "row", sm: "row" },
            }}
          >
            <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: "8px", flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="70%" height={16} />
              <Skeleton variant="text" width="40%" height={14} sx={{ mt: 0.5 }} />
            </Box>
            <Skeleton variant="text" width={50} height={16} />
          </Box>
          {/* Second item skeleton */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              p: 1.5,
              borderRadius: "10px",
              border: "1px solid #f0f0f0",
              flexDirection: { xs: "row", sm: "row" },
            }}
          >
            <Skeleton variant="rectangular" width={64} height={64} sx={{ borderRadius: "8px", flexShrink: 0 }} />
            <Box sx={{ flexGrow: 1 }}>
              <Skeleton variant="text" width="60%" height={16} />
              <Skeleton variant="text" width="35%" height={14} sx={{ mt: 0.5 }} />
            </Box>
            <Skeleton variant="text" width={45} height={16} />
          </Box>
        </Box>
      </Box>
      {/* Footer Skeleton */}
      <Box
        sx={{
          p: { xs: 2, sm: 3 },
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", sm: "center" },
          gap: 2,
        }}
      >
        <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
          <Skeleton variant="rectangular" width={120} height={32} sx={{ borderRadius: "24px" }} />
          <Skeleton variant="rectangular" width={140} height={32} sx={{ borderRadius: "24px" }} />
        </Box>
      </Box>
    </Paper>
  );

  return (
    <Container
      maxWidth="md"
      sx={{
        py: { xs: 3, md: 6 },
        color: "black",
      }}
    >
      {/* Header */}
      <Box
  sx={{
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    mb: { xs: 3, md: 5 },
  }}
>
  <Typography
    variant="h4"
    sx={{
      fontSize: { xs: "26px", sm: "32px", md: "40px" },
      fontWeight: 700,
      color: "#111",
    }}
  >
    My Orders
  </Typography>

  <Box>
    <IconButton
      aria-label="more options"
      onClick={handleFilterMenuOpen}
      sx={{ color: "#111" }}
    >
      <MoreVertIcon />
    </IconButton>
    <Menu
      anchorEl={filterMenuAnchor}
      open={Boolean(filterMenuAnchor)}
      onClose={handleFilterMenuClose}
      PaperProps={{
        sx: {
          boxShadow: "0 4px 16px rgba(0,0,0,0.1)",
          borderRadius: "12px",
        },
      }}
    >
      <MenuItem 
        onClick={() => handleStatusSelect("")}
        selected={selectedStatus === ""}
        sx={{ 
          fontWeight: selectedStatus === "" ? 600 : 400,
          color: selectedStatus === "" ? "black" : "inherit"
        }}
      >
        All Orders
      </MenuItem>
      <MenuItem 
        onClick={() => handleStatusSelect("confirmed")}
        selected={selectedStatus === "confirmed"}
        sx={{ 
          fontWeight: selectedStatus === "confirmed" ? 600 : 400,
          color: selectedStatus === "confirmed" ? "black" : "inherit"
        }}
      >
        Confirmed
      </MenuItem>
      <MenuItem 
        onClick={() => handleStatusSelect("delivered")}
        selected={selectedStatus === "delivered"}
        sx={{ 
          fontWeight: selectedStatus === "delivered" ? 600 : 400,
          color: selectedStatus === "delivered" ? "black" : "inherit"
        }}
      >
        Delivered
      </MenuItem>
      <MenuItem 
        onClick={() => handleStatusSelect("cancelled")}
        selected={selectedStatus === "cancelled"}
        sx={{ 
          fontWeight: selectedStatus === "cancelled" ? 600 : 400,
          color: selectedStatus === "cancelled" ? "black" : "inherit"
        }}
      >
        Cancelled
      </MenuItem>
      <MenuItem 
        onClick={() => handleStatusSelect("return")}
        selected={selectedStatus === "return"}
        sx={{ 
          fontWeight: selectedStatus === "return" ? 600 : 400,
          color: selectedStatus === "return" ? "black" : "inherit"
        }}
      >
        Return
      </MenuItem>
    </Menu>
  </Box>
</Box>

      {/* Loading State - Show skeleton loaders */}
      {loading && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <OrderSkeleton />
          <OrderSkeleton />
          <OrderSkeleton />
        </Box>
      )}

      {/* Error State */}
      {error && !loading && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Orders List */}
      {!loading && !error && (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {orders.length === 0 ? (
           <Box
  sx={{
    textAlign: "center",
    py: 5,           // vertical padding
    px: 2,           // horizontal padding for small screens
  }}
>
  <Box
    component="img"
    src="/img/noOrder.png"
    alt="No Orders"
    sx={{
      maxWidth: "100%",
      height: "auto",     // margin below image
    }}
  />
  <Typography
    variant="h6"
    sx={{
      color: "text.secondary",
    }}
  >
    No orders found
  </Typography>
</Box>
          ) : (
            orders.map((order) => (
          <Paper
            key={order.id}
            elevation={1}
            sx={{
              // border: "1px solid black",
              bgcolor: "white",
              overflow: "hidden",
               boxShadow: "0 4px 16px rgba(0,0,0,0.05)",
              borderRadius:"20px"
            }}
          >
            {/* Order Header */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Grid container alignItems="center" sx={{justifyContent:"space-between"}} spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <Avatar
                      sx={{
                        bgcolor: "black",
                        width: 42,
                        height: 42,
                      }}
                    >
                      {getStatusIcon(order.status)}
                    </Avatar>
                    <Box>
                      <Typography
                        variant="h6"
                        fontWeight={600}
                        sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                      >
                        {order.id}
                      </Typography>
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary", fontSize: "13px" }}
                      >
                        Ordered on {order.date}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: {
                        xs: "flex-start",
                        sm: "flex-end",
                      },
                      alignItems: "center",
                      gap: 1.5,
                      flexWrap: "wrap",
                    }}
                  >
                    <Chip
                      label={order.status.toUpperCase()}
                      sx={{
                        fontWeight: 600,
                        color: "white",
                        bgcolor: "black",
                        borderRadius: "20px",
                        fontSize: "12px",
                        px: 1.5,
                      }}
                    />
                    <Typography
                      variant="subtitle1"
                      fontWeight={700}
                      sx={{ fontSize: "15px" }}
                    >
                      ₹{order.total}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>
            <Divider />
            {/* Order Items */}
            <Box sx={{ p: { xs: 2, sm: 3 } }}>
              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 1.5, color: "#111" }}
              >
                Order Items
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                {order.items.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 1.5,
                      borderRadius: "10px",
                      border: "1px solid #f0f0f0",
                      flexDirection: { xs: "row", sm: "row" },
                    }}
                  >
                    <Avatar
                      src={item.image}
                      variant="rounded"
                      sx={{
                        width: 64,
                        height: 64,
                        border: "1px solid #ddd",
                        flexShrink: 0,
                      }}
                    />
                    <Box sx={{ flexGrow: 1 }}>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ fontSize: "14px" }}
                      >
                        {item.name}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ fontSize: "13px" }}
                      >
                        Qty: {item.quantity}
                      </Typography>
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ fontSize: "14px" }}
                    >
                      ₹{item.price}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
            {/* Footer */}
            {/* <Divider /> */}
            <Box
              sx={{
                p: { xs: 2, sm: 3 },
                display: "flex",
                flexDirection: { xs: "column", sm: "row" },
                justifyContent: "space-between",
                alignItems: { xs: "flex-start", sm: "center" },
                gap: 2,
              }}
            >
              <Box sx={{ display: "flex", gap: 1.5, flexWrap: "wrap" }}>
                <Button
                  variant="outlined"
                  startIcon={<Visibility />}
                  size="small"
                  disabled={!order._id}
                  onClick={() => {
                    if (order._id) {
                      navigate(`/orderdetails/${order._id}`);
                    } else {
                      console.error("Order ID is missing. Order data:", order);
                    }
                  }}
                  sx={{
                    color: "black",
                    borderColor: "black",
                    borderRadius: "24px",
                    textTransform: "none",
                    px: 2.5,
                    "&:hover": { bgcolor: "black", color: "white" },
                    "&:disabled": {
                      borderColor: "#ccc",
                      color: "#ccc",
                    },
                  }}
                >
                  View Details
                </Button>
                {/* {order.status === "delivered" && (
                  <Button
                    variant="outlined"
                    startIcon={<Star />}
                    size="small"
                    onClick={() => handleOpenReview(order.id)}
                    sx={{
                      color: "black",
                      borderColor: "black",
                      borderRadius: "24px",
                      textTransform: "none",
                      px: 2.5,
                      "&:hover": { bgcolor: "black", color: "white" },
                    }}
                  >
                    Rate & Review
                  </Button>
                )} */}
              </Box>
            </Box>
          </Paper>
            ))
          )}
        </Box>
      )}

      {/* Rate & Review Modal */}
      {/* <ReviewModal
        open={reviewModal.open}
        orderId={reviewModal.orderId}
        onClose={handleCloseReview}
        onSubmit={handleSubmitReview}
      /> */}
    </Container>
  );
}

export default MyOrders;